export type MetricDirection = "positive" | "negative" | "neutral";

export type InsightActionKind =
    | "campaign"
    | "analysis"
    | "automation"
    | "settings"
    | "report"
    | "cogs"
    | "ai_task"
    | "ops_task"
    | "alert"
    | "comms"
    | "pos"
    | "schedule"
    | "labor"
    | "coaching"
    | "checklist";

export interface InsightWorkflowBullet {
    label: string;
    value: string;
}

export interface InsightWorkflow {
    intro: string;
    bullets?: InsightWorkflowBullet[];
    followUp?: string;
    quickReplies: string[];
}

/**
 * Seeds a live drilldown report opened from an insight action (e.g. the Store Health
 * "Review Log" action) with the transaction metric codes, date range, and grouping the
 * originating card fired on. Refinable via the workflow's LLM prompt box.
 */
export interface ReportContext {
    metricCodes: string[];
    fromDate: string;
    toDate: string;
    /** "Employee", "Day", or "Location". */
    groupBy: string;
}

export interface InsightActionCard {
    id: string;
    kind: InsightActionKind;
    category: string;
    title: string;
    description: string;
    ctaLabel: string;
    workflow: InsightWorkflow;
    reportContext?: ReportContext;
}

export interface HeadlineMetric {
    value: string;
    label: string;
    direction: MetricDirection;
}

export interface InsightTracker {
    metricLabel: string;
    actual: string;
    goal: string;
}

export interface InsightCard {
    id: string;
    source: string;
    confidence: number;
    headlineMetric: HeadlineMetric;
    title: string;
    tags: string[];
    narrative: string;
    aiNote?: string;
    actions: InsightActionCard[];
    recommendedActionId?: string;
    /** Actual-vs-goal progress tracker shown while the insight is being followed up. */
    tracker?: InsightTracker;
    /** Punchh campaign id this insight targets. Populated by live insight generators. */
    campaignId?: number;
}

export const enrollmentDropCard: InsightCard = {
    id: "ins_enrollment_drop",
    source: "PAR Punchh® · Loyalty Enrollment",
    confidence: 88,
    headlineMetric: {
        value: "+1,200",
        label: "New members / mo at no acquisition cost",
        direction: "positive"
    },
    title: "Enrollment Drop — In-Store Capture",
    tags: ["In-Store Capture", "Bottom 20 Locations", "11.8% Benchmark"],
    narrative:
        "Loyalty enrollment is down 14% over the last 30 days. In-app sign-ups are up 9% — the drop comes entirely from in-store capture. Bottom 20 locations average a 4.2% enrollment rate; top 20 average 11.8% — right at the QSR benchmark. Closing the gap could add ~1,200 new members/month at no acquisition cost.",
    aiNote: "Bottom-quartile locations share a common trait — no POS enrollment prompt is configured. Enabling the prompt at these stores alone could close roughly 70% of the enrollment gap.",
    actions: [
        {
            id: "act_pos_prompt",
            kind: "settings",
            category: "Configure POS",
            title: "Configure POS enrollment prompt cadence",
            description:
                "In PAR POS settings, add a loyalty sign-up prompt screen at low-enrollment locations — e.g. trigger it on every 5th non-member transaction.",
            ctaLabel: "Configure Prompt",
            workflow: {
                intro: "Here's the POS enrollment prompt configuration for the bottom 20 locations:",
                bullets: [
                    {
                        label: "Scope",
                        value: "20 lowest-enrollment locations (under 5% capture rate)"
                    },
                    {
                        label: "Trigger",
                        value: "Every 5th non-member transaction at the register"
                    },
                    {
                        label: "Prompt",
                        value: "Single-tap phone-number sign-up with instant first reward"
                    },
                    {
                        label: "Rollout",
                        value: "Staged — enable 5 stores, measure for 7 days, then expand"
                    }
                ],
                followUp:
                    "Want me to estimate the enrollment lift per store before we push this live to all 20 locations?",
                quickReplies: [
                    "Adjust the trigger frequency",
                    "Pick the pilot stores",
                    "Preview the prompt screen",
                    "Estimate the lift"
                ]
            }
        }
    ]
};

export const winBackCard: InsightCard = {
    id: "ins_win_back",
    source: "PAR Punchh® · Member Retention",
    confidence: 84,
    headlineMetric: {
        value: "$34K",
        label: "Recoverable revenue this quarter",
        direction: "positive"
    },
    title: "At-Risk Members — Win-Back Window",
    tags: ["8,400 At-Risk", "+22% vs Last Qtr", "45+ Days Inactive"],
    narrative:
        "8,400 members who visited 2×/month are now 45+ days inactive — up 22% from last quarter. A targeted win-back now could recover ~18% (~$34K in revenue). Lapsed members who used to be frequent visitors almost never return once they cross 60 days.",
    aiNote: "Recovery probability drops sharply after 60 days inactive. Acting on the 45–59 day window captures the steepest part of the win-back curve before these members are effectively lost.",
    actions: [
        {
            id: "act_winback_campaign",
            kind: "campaign",
            category: "Launch Campaign",
            title: "Launch win-back campaign for at-risk segment",
            description:
                'One-click create a Punchh campaign targeting the 8,400 at-risk members (45–59 days inactive) with a personalized "We miss you" offer before they cross the 60-day threshold.',
            ctaLabel: "Launch Campaign",
            workflow: {
                intro: "Here's the win-back campaign setup for the at-risk segment:",
                bullets: [
                    {
                        label: "Audience",
                        value: "8,400 members · 45–59 days inactive · formerly 2+ visits/mo"
                    },
                    {
                        label: "Offer",
                        value: 'Personalized "We miss you" reward, redeemable in 14 days'
                    },
                    {
                        label: "Channel",
                        value: "Push + email, sent at each member's typical visit time"
                    },
                    {
                        label: "Goal",
                        value: "Recover ~18% before they cross the 60-day churn threshold"
                    }
                ],
                followUp:
                    "Want me to A/B test a bonus-points offer against a free-item offer to see which recovers more members?",
                quickReplies: [
                    "Adjust the audience",
                    "Draft the message",
                    "Set up an A/B test",
                    "How much will this cost?"
                ]
            }
        },
        {
            id: "act_churn_automation",
            kind: "automation",
            category: "Set Automation",
            title: "Set up churn early-warning automation",
            description:
                "Configure a Punchh rule: if a member who visited 2+×/month goes 30 days without a visit, auto-enroll them in a re-engagement journey — a bonus-points nudge at day 30 and an offer at day 42.",
            ctaLabel: "Set Up Automation",
            workflow: {
                intro: "Here's the churn early-warning automation rule:",
                bullets: [
                    {
                        label: "Trigger",
                        value: "Member averaging 2+ visits/mo goes 30 days with no visit"
                    },
                    {
                        label: "Day 30",
                        value: 'Bonus-points nudge — "Your points are waiting"'
                    },
                    {
                        label: "Day 42",
                        value: "Personalized offer before the 60-day churn cliff"
                    },
                    {
                        label: "Exit",
                        value: "Member returns and transacts — auto-removed from journey"
                    }
                ],
                followUp:
                    "Should this run continuously for every qualifying member, or start as a 60-day pilot so we can measure the recovery rate first?",
                quickReplies: [
                    "Adjust the timing",
                    "Edit the nudges",
                    "Run as a pilot first",
                    "Project the impact"
                ]
            }
        }
    ]
};

export const silverTierCard: InsightCard = {
    id: "ins_silver_tier",
    source: "PAR Punchh® · New Member Cohorts",
    confidence: 91,
    headlineMetric: {
        value: "~$180K",
        label: "Incremental annual rev.",
        direction: "positive"
    },
    title: "Member LTV / Silver Tier Drop-off",
    tags: [
        "61% Never Reach Silver",
        "Drop-off at Visits 3–5",
        "2.1× LTV at Early Silver"
    ],
    narrative:
        "Loyalty members spend 23% more per visit. Members reaching Silver within 90 days go on to spend 2.1× their first-year value; slow-to-Silver members only 1.3×. But 61% of new members never reach Silver — drop-off happens at visits 3–5. Moving 10% more members to early Silver could yield ~$180K incremental annual revenue.",
    aiNote: "Current tier threshold puts Silver at the 78th percentile of visit frequency for new members. A 12-point reduction would nearly double early-Silver attainment without materially eroding reward economics.",
    actions: [
        {
            id: "act_silver_campaign",
            kind: "campaign",
            category: "Launch Campaign",
            title: "Create Early-Silver Accelerator Campaign",
            description:
                'Launch a Punchh "Fast Track to Silver" campaign for new members within their first 30 days: bonus points on visits 3, 4, and 5 — targeting exactly where drop-off is highest.',
            ctaLabel: "Launch Campaign",
            workflow: {
                intro: "Here's the campaign setup for the Silver accelerator:",
                bullets: [
                    {
                        label: "Audience",
                        value: "New members · within first 30 days · not yet at Silver"
                    },
                    {
                        label: "Mechanic",
                        value: "Bonus points on visits 3, 4, and 5 — where 61% of drop-off happens"
                    },
                    {
                        label: "Suggested bonus",
                        value: "2× points on each of those visits"
                    },
                    {
                        label: "Duration",
                        value: "Rolling — auto-applies to every new member cohort"
                    }
                ],
                followUp:
                    "Want me to model what 2× vs 3× points does to your reward economics before we launch?",
                quickReplies: [
                    "Adjust the audience",
                    "Draft the copy",
                    "Set up an A/B test",
                    "How much will this cost?"
                ]
            }
        },
        {
            id: "act_silver_threshold",
            kind: "analysis",
            category: "Run Analysis",
            title: "Model Tier Threshold Optimization",
            description:
                "Run an AI analysis: given current visit-frequency distributions, what Silver threshold would maximize the % of members who reach it within 90 days without eroding reward economics?",
            ctaLabel: "Run Analysis",
            workflow: {
                intro: "Here's how I'll model the tier-threshold optimization:",
                bullets: [
                    {
                        label: "Inputs",
                        value: "90-day visit-frequency distribution for the last 4 new-member cohorts"
                    },
                    {
                        label: "Objective",
                        value: "Maximize % reaching Silver in 90 days, hold reward cost flat"
                    },
                    {
                        label: "Constraint",
                        value: "Per-member reward economics stay within ±3% of today"
                    },
                    {
                        label: "Output",
                        value: "Recommended points threshold + projected attainment curve"
                    }
                ],
                followUp:
                    "I can run this against the current threshold and three candidate thresholds. Want the full sensitivity sweep or just the single best recommendation?",
                quickReplies: [
                    "Run the full sweep",
                    "Just the best threshold",
                    "Add a revenue projection",
                    "Explain the method"
                ]
            }
        },
        {
            id: "act_silver_nudge",
            kind: "automation",
            category: "Set Automation",
            title: "Trigger Personalized Nudge at Visit 3",
            description:
                "Configure a Punchh journey: after a member's 3rd visit, send a push showing exactly how many more points they need for Silver — and what reward awaits — to sustain momentum through the drop-off window.",
            ctaLabel: "Set Up Journey",
            workflow: {
                intro: "Here's the visit-3 nudge journey:",
                bullets: [
                    {
                        label: "Trigger",
                        value: "Member completes their 3rd qualifying visit"
                    },
                    {
                        label: "Message",
                        value: '"You\'re {X} points from Silver" with the unlocked reward shown'
                    },
                    {
                        label: "Channel",
                        value: "Push notification, sent within 1 hour of the visit"
                    },
                    {
                        label: "Follow-through",
                        value: "Reminder at visit 4 if still below threshold"
                    }
                ],
                followUp:
                    "Want a second touch at visit 5 for members who still haven't crossed, or keep it to the visit-3 nudge only?",
                quickReplies: [
                    "Add a visit-5 touch",
                    "Edit the message",
                    "Set quiet hours",
                    "Preview the push"
                ]
            }
        }
    ]
};

export const offerIncrementalityCard: InsightCard = {
    id: "ins_offer_incrementality",
    source: "PAR Punchh® · Offer Performance",
    confidence: 89,
    headlineMetric: {
        value: "84%",
        label: "Free Dessert redeemers would visit anyway",
        direction: "negative"
    },
    title: "Offer Incrementality / Budget Reallocation",
    tags: ["Low Incrementality", "$2.10 / Redemption", "1.4× Lift on Double Points"],
    narrative:
        '"Free Dessert" has a 31% redemption rate but 84% of redeemers visited anyway — costing $2.10 per redemption with low incrementality. "Double Points Tuesday" has 12% redemption but drove a 1.4× visit lift and is cheaper per redemption. Budget should shift toward offers that move behavior, not reward it.',
    aiNote: "Double Points Tuesday delivers roughly 3× the incremental visits per dollar of Free Dessert. Reallocating even 30% of the dessert budget would lift net-new visits without raising total offer spend.",
    actions: [
        {
            id: "act_reallocate_budget",
            kind: "settings",
            category: "Reallocate Budget",
            title: "Reallocate offer budget in Punchh",
            description:
                'In Punchh campaign settings, reduce the "Free Dessert" offer cap for high-frequency members and redirect that budget to a lapsed-member version of the Double Points event where incrementality is cleanest.',
            ctaLabel: "Open Budget Settings",
            workflow: {
                intro: "Here's the proposed budget reallocation:",
                bullets: [
                    {
                        label: "Reduce",
                        value: "Free Dessert cap for high-frequency members (visited in last 14 days)"
                    },
                    {
                        label: "Redirect",
                        value: "Freed budget to a lapsed-member Double Points event"
                    },
                    {
                        label: "Net spend",
                        value: "Flat — no increase to total offer budget"
                    },
                    {
                        label: "Expected",
                        value: "Higher net-new visits per dollar of offer cost"
                    }
                ],
                followUp:
                    "Want me to start with a 30% shift and scale up if incrementality holds, or move the full recommended amount now?",
                quickReplies: [
                    "Start with 30%",
                    "Move the full amount",
                    "See the per-offer math",
                    "Set a guardrail"
                ]
            }
        },
        {
            id: "act_incrementality_analysis",
            kind: "analysis",
            category: "Run Analysis",
            title: "Run LLM Incrementality Analysis",
            description:
                "Trigger an AI research task to model incrementality across all active campaigns: for each offer, estimate what % of redemptions are net-new visits using visit-cadence baselines. Surface the top 3 high-ROI offers.",
            ctaLabel: "Run Analysis",
            workflow: {
                intro: "Here's how I'll run the cross-campaign incrementality analysis:",
                bullets: [
                    {label: "Scope", value: "All active campaigns this quarter"},
                    {
                        label: "Method",
                        value: "Compare redeemer visit cadence vs. their pre-offer baseline"
                    },
                    {
                        label: "Metric",
                        value: "% of redemptions that are net-new visits + cost per incremental visit"
                    },
                    {
                        label: "Output",
                        value: "Ranked offers with the top 3 high-ROI campaigns highlighted"
                    }
                ],
                followUp:
                    "Should I include offers that ended in the last 90 days for a broader benchmark, or keep it to currently active campaigns only?",
                quickReplies: [
                    "Active only",
                    "Include recent offers",
                    "Add a confidence score",
                    "Explain the baseline"
                ]
            }
        },
        {
            id: "act_gate_offers",
            kind: "settings",
            category: "Update Eligibility",
            title: "Gate free-item offers to lapsed members only",
            description:
                'Update the "Free Dessert" offer eligibility rules in Punchh to exclude members who visited in the last 14 days — ensuring the offer only reaches people who actually need the pull, not regular visitors.',
            ctaLabel: "Edit Offer Rules",
            workflow: {
                intro: "Here's the updated Free Dessert eligibility rule:",
                bullets: [
                    {
                        label: "Exclude",
                        value: "Members who visited within the last 14 days"
                    },
                    {
                        label: "Include",
                        value: "Lapsed and infrequent members who need the pull"
                    },
                    {
                        label: "Effect",
                        value: "Stops paying for visits that would have happened anyway"
                    },
                    {
                        label: "Reversible",
                        value: "Rule can be rolled back instantly if redemption drops too far"
                    }
                ],
                followUp:
                    "Want to test a 14-day window against a 21-day window to find where incrementality is best, or apply the 14-day rule now?",
                quickReplies: [
                    "Apply the 14-day rule",
                    "Test 14 vs 21 days",
                    "Preview affected members",
                    "Set an end date"
                ]
            }
        }
        /* Trimmed to keep the actions list to 3 — restore if needed.
        {
            id: "act_roi_report",
            kind: "report",
            category: "Generate Report",
            title: "Create a campaign ROI report",
            description:
                "Generate a shareable Punchh report comparing cost-per-incremental-visit across all campaigns this quarter and email it to marketing leadership — to inform Q3 offer strategy planning.",
            ctaLabel: "Create Report",
            workflow: {
                intro: "Here's the campaign ROI report I'll generate:",
                bullets: [
                    {
                        label: "Compares",
                        value: "Cost per incremental visit across every campaign this quarter"
                    },
                    {
                        label: "Highlights",
                        value: "Best and worst performers, with the budget-shift recommendation"
                    },
                    {
                        label: "Format",
                        value: "Shareable Punchh report + one-page executive summary"
                    },
                    {
                        label: "Delivery",
                        value: "Emailed to marketing leadership for Q3 planning"
                    }
                ],
                followUp:
                    "Want me to add a recommended Q3 budget allocation to the summary, or keep the report to performance data only?",
                quickReplies: [
                    "Add a Q3 recommendation",
                    "Performance data only",
                    "Choose recipients",
                    "Schedule it monthly"
                ]
            }
        }
        */
    ]
};

export const cogsVarianceTrendCard: InsightCard = {
    id: "ins_cogs_variance_trend",
    source: "PAR Operations · COGS Variance",
    confidence: 86,
    headlineMetric: {
        value: "5.8%",
        label: "Total variance — down from 7.9%",
        direction: "positive"
    },
    title: "Variance Trending Down — Gap Concentrated",
    tags: ["7.9% → 7.0% → 5.8%", "Above 2–3% Healthy", "Few Items Drive Leakage"],
    narrative:
        "Total variance is trending in the right direction — 7.9% → 7.0% → 5.8% — but still well above the 2–3% healthy range. The same handful of items are responsible for gross leakage every period. Progress is real, but the remaining gap is concentrated and controllable.",
    aiNote: "The decline correlates with portioning corrections on two proteins. Reinforcing that same discipline on the remaining top-variance items should close most of the gap to the healthy range.",
    actions: [
        {
            id: "act_cogs_research_improvement",
            kind: "ai_task",
            category: "AI Task",
            title: "Research what's driving the improvement",
            description:
                "Trigger an AI task to cross-reference the variance decline with recent operational changes (new hires, schedule changes, training events, menu changes) and generate a hypothesis for what's actually working — so you can reinforce it intentionally.",
            ctaLabel: "Run Research",
            workflow: {
                intro: "Here's how I'll research what's driving the improvement:",
                bullets: [
                    {
                        label: "Window",
                        value: "Last 3 periods, aligned to the 7.9% → 5.8% decline"
                    },
                    {
                        label: "Cross-reference",
                        value: "New hires, schedule changes, training events, menu changes"
                    },
                    {
                        label: "Output",
                        value: "Ranked hypotheses for what's working, with supporting evidence"
                    },
                    {
                        label: "Goal",
                        value: "Reinforce the winning behaviors intentionally, not by accident"
                    }
                ],
                followUp:
                    "Want me to weight operational changes by how closely they line up with the periods that improved most?",
                quickReplies: [
                    "Weight by timing",
                    "Include labor data",
                    "Just the top hypothesis",
                    "Explain the method"
                ]
            }
        },
        {
            id: "act_cogs_benchmark_locations",
            kind: "ai_task",
            category: "AI Task",
            title: "Benchmark against similar locations",
            description:
                "Pull variance % across comparable locations (same format, volume tier) to contextualize whether this location's 5.8% is above or below the peer average — and surface the lowest-variance peer as a best-practice reference.",
            ctaLabel: "Run Benchmark",
            workflow: {
                intro: "Here's the peer benchmark I'll run:",
                bullets: [
                    {
                        label: "Peer set",
                        value: "Same format and volume tier as this location"
                    },
                    {
                        label: "Metric",
                        value: "Variance % this period vs. the peer average"
                    },
                    {
                        label: "Surface",
                        value: "Lowest-variance peer as a best-practice reference"
                    },
                    {
                        label: "Output",
                        value: "Where 5.8% ranks, and what the top peer does differently"
                    }
                ],
                followUp:
                    "Should I limit the peer set to your region, or compare against all comparable locations nationally?",
                quickReplies: [
                    "Region only",
                    "Compare nationally",
                    "Show the top peer",
                    "Add a percentile rank"
                ]
            }
        }
    ]
};

export const cogsTopOffenderCard: InsightCard = {
    id: "ins_cogs_top_offender",
    source: "PAR Operations · COGS Waste",
    confidence: 90,
    headlineMetric: {
        value: "$130",
        label: "Rolls Large White overage / period",
        direction: "negative"
    },
    title: "Top Offender — Waste + Variance Double Problem",
    tags: ["Rolls Large White", "Over-Portioned + Wasted", "Turkey Still High"],
    narrative:
        "Rolls Large White is the top problem: consistently over by $125–135 per period and leading in waste dollars — meaning it's both over-portioned and physically thrown away. Roast beef dropped from ~$70+ to ~$1 variance, showing that portioning discipline works when applied. Turkey is still running high.",
    aiNote: "Roast beef's drop from $70+ to ~$1 proves the fix works. Applying the same portioning correction to Rolls Large White and turkey is the highest-leverage next move.",
    actions: [
        {
            id: "act_cogs_waste_threshold_alert",
            kind: "alert",
            category: "Alert",
            title: "Set a waste dollar threshold alert",
            description:
                "Configure a COGS alert that fires during the period if logged waste for a single item crosses a dollar threshold (e.g. $50) — giving the manager a chance to intervene mid-period rather than discovering it at close.",
            ctaLabel: "Configure Alert",
            workflow: {
                intro: "Here's the waste-dollar threshold alert:",
                bullets: [
                    {
                        label: "Trigger",
                        value: "Logged waste for a single item crosses $50 in a period"
                    },
                    {
                        label: "Scope",
                        value: "Starts with Rolls Large White and turkey, expandable to all items"
                    },
                    {
                        label: "Timing",
                        value: "Fires mid-period — before close, not after"
                    },
                    {
                        label: "Routed to",
                        value: "Store manager, with the item and running waste total"
                    }
                ],
                followUp:
                    "Want a single $50 threshold for every item, or per-item thresholds scaled to each item's normal cost?",
                quickReplies: [
                    "Use $50 for all",
                    "Scale per item",
                    "Pick the items",
                    "Set who gets alerted"
                ]
            }
        }
    ]
};

export const cogsClassificationCard: InsightCard = {
    id: "ins_cogs_classification",
    source: "PAR Operations · COGS Signal",
    confidence: 88,
    headlineMetric: {
        value: "$40–49",
        label: "Cucumber variance — likely UOM error",
        direction: "neutral"
    },
    title: "Systemic vs. Noise — Stop Chasing Ghosts",
    tags: ["Cucumbers: UOM Error", "Systemic Over-Usage", "3-Period Signal"],
    narrative:
        "Cucumbers show $40–49 over-usage across 3 periods on tiny actual usage — almost certainly a count or unit-of-measure error, not real consumption. Proteins and produce show systemic over-usage. Distinguishing recurring signal from count-timing noise lets managers stop chasing ghosts and fix what's real.",
    aiNote: "Separating systemic items from count-timing noise typically clears 30–40% of flagged items from the action queue — letting the team focus only on real, recurring leakage.",
    actions: [
        {
            id: "act_cogs_auto_classify",
            kind: "cogs",
            category: "COGS",
            title: "Auto-classify items by variance pattern",
            description:
                'Apply a 3-period classification to every item: "systemic" (consistently over or under by >10% of theoretical), "noise" (flip-flops or near-zero), or "data error" (large variance on very low usage). Surface only systemic items in the action queue.',
            ctaLabel: "Run Classification",
            workflow: {
                intro: "Here's the 3-period variance classification:",
                bullets: [
                    {
                        label: "Systemic",
                        value: "Consistently over or under by >10% of theoretical"
                    },
                    {
                        label: "Noise",
                        value: "Flip-flops period to period, or near-zero variance"
                    },
                    {
                        label: "Data error",
                        value: "Large variance on very low actual usage"
                    },
                    {
                        label: "Result",
                        value: "Action queue shows only systemic items worth fixing"
                    }
                ],
                followUp:
                    "Want to keep the >10% systemic threshold, or tune it to your location's typical noise level first?",
                quickReplies: [
                    "Keep 10%",
                    "Tune the threshold",
                    "Preview the split",
                    "Explain the rules"
                ]
            }
        },
        {
            id: "act_cogs_flag_uom_error",
            kind: "cogs",
            category: "COGS",
            title: "Flag cucumber as a data / UOM error",
            description:
                "Automatically surface items where variance is large but usage is small — a near-certain sign of a unit-of-measure mismatch (e.g. counted in each vs. case). Create a task for the manager to verify the count unit in the system and correct it.",
            ctaLabel: "Flag Item",
            workflow: {
                intro: "Here's the data / UOM error flag for cucumbers:",
                bullets: [
                    {
                        label: "Signal",
                        value: "Large variance dollars on very small actual usage"
                    },
                    {
                        label: "Likely cause",
                        value: "Unit-of-measure mismatch — counted in each vs. case"
                    },
                    {
                        label: "Task",
                        value: "Manager verifies the count unit in the system and corrects it"
                    },
                    {
                        label: "Effect",
                        value: "Removes a recurring false positive from the variance report"
                    }
                ],
                followUp:
                    "Want me to scan for other items with the same large-variance-on-tiny-usage pattern while I'm at it?",
                quickReplies: [
                    "Scan for similar items",
                    "Just cucumbers",
                    "Assign the task",
                    "Show the math"
                ]
            }
        },
        {
            id: "act_cogs_noise_to_systemic_alert",
            kind: "alert",
            category: "Alert",
            title: "Alert when a noise item becomes systemic",
            description:
                'Set a COGS rule: if an item previously classified as "noise" shows consistent directional variance (same sign, >$20) for two periods in a row, reclassify it as "systemic" and surface it in the action queue — catching problems before they compound.',
            ctaLabel: "Set Alert",
            workflow: {
                intro: "Here's the noise-to-systemic escalation rule:",
                bullets: [
                    {label: "Watch", value: "Items currently classified as noise"},
                    {
                        label: "Trigger",
                        value: "Same-sign variance over $20 for two periods in a row"
                    },
                    {
                        label: "Action",
                        value: "Auto-reclassify as systemic and add to the action queue"
                    },
                    {
                        label: "Benefit",
                        value: "Catches emerging problems before they compound"
                    }
                ],
                followUp:
                    "Want the $20 / two-period trigger, or a tighter rule that escalates after a single large period?",
                quickReplies: [
                    "Use $20 / 2 periods",
                    "Escalate faster",
                    "Notify on escalation",
                    "Explain the rule"
                ]
            }
        }
    ]
};

export const cogsRecoveryCard: InsightCard = {
    id: "ins_cogs_recovery",
    source: "PAR Operations · COGS Recovery",
    confidence: 92,
    headlineMetric: {
        value: "$4,233",
        label: "Estimated annual recovery",
        direction: "positive"
    },
    title: "COGS Recovery Opportunity — Top 5 Items",
    tags: ["$4,233 / yr Recoverable", "Top 5 = Majority", "Rolls ~$65 / period"],
    narrative:
        "Estimated annual recovery across systemic items is $4,233. Rolls Large White alone represents ~$65/period recoverable. Proteins (Turkey, Steak, Chicken, Capicola) and produce/condiments (Lettuce, Peppers, Mayo, Oil Dressing, Pizza Sauce) make up the remaining concentration. Fixing the top 5 items covers the majority of the opportunity.",
    aiNote: "Recovery is concentrated — the top 5 items hold most of the $4,233. A focused fix list with one intervention per item makes the opportunity tractable in a single period.",
    actions: [
        {
            id: "act_cogs_prioritized_fix_list",
            kind: "cogs",
            category: "COGS",
            title: "Generate a prioritized fix list by dollar recovery",
            description:
                "Rank every systemic over-usage item by estimated annual recovery dollars and surface the top 5 as a focused action list — with one recommended intervention per item and the projected margin impact of a 50% improvement on each.",
            ctaLabel: "Generate Fix List",
            workflow: {
                intro: "Here's the prioritized fix list I'll generate:",
                bullets: [
                    {
                        label: "Rank by",
                        value: "Estimated annual recovery dollars per item"
                    },
                    {label: "Focus", value: "Top 5 systemic over-usage items"},
                    {
                        label: "Each item",
                        value: "One recommended intervention (par, portioning, count)"
                    },
                    {
                        label: "Projection",
                        value: "Margin impact of a 50% improvement on each"
                    }
                ],
                followUp:
                    "Want the top 5, or extend the list to cover everything down to a minimum recovery threshold?",
                quickReplies: [
                    "Top 5 only",
                    "Use a $ threshold",
                    "Add intervention owners",
                    "Show the projections"
                ]
            }
        },
        {
            id: "act_cogs_manager_action_plan",
            kind: "ops_task",
            category: "Ops Task",
            title: "Create a manager action plan for top 3 items",
            description:
                "Auto-generate a structured weekly action plan for the top 3 recovery items — assigning a specific intervention (par change, portioning audit, count correction) with a due date, responsible party, and a check-in trigger after one period.",
            ctaLabel: "Create Action Plan",
            workflow: {
                intro: "Here's the weekly action plan for the top 3 recovery items:",
                bullets: [
                    {
                        label: "Per item",
                        value: "A specific intervention — par change, portioning audit, or count correction"
                    },
                    {
                        label: "Accountability",
                        value: "Due date and a responsible party for each"
                    },
                    {
                        label: "Check-in",
                        value: "Auto-trigger to review progress after one period"
                    },
                    {label: "Scope", value: "Top 3 items by recoverable dollars"}
                ],
                followUp:
                    "Want me to assign owners automatically by role, or leave the responsible party for the manager to fill in?",
                quickReplies: [
                    "Assign by role",
                    "Manager fills in",
                    "Set the due dates",
                    "Add a fourth item"
                ]
            }
        },
        {
            id: "act_cogs_portioning_audit",
            kind: "checklist",
            category: "Checklist",
            title: "Assign portioning audit",
            description:
                "Launch a step-by-step portioning audit checklist targeting the top over-usage items — Rolls, Turkey, Steak, Chicken, Capicola, Lettuce, Peppers, Mayo, Oil Dressing, and Pizza Sauce — and assign each step to a responsible team member.",
            ctaLabel: "Open Checklist",
            workflow: {
                intro: "Opening the portioning audit checklist for your top COGS offenders:",
                bullets: [
                    {
                        label: "Scope",
                        value: "Top 5 over-usage items by recovery dollar (Rolls, Turkey, Steak, Chicken, Capicola)"
                    },
                    {
                        label: "Also covering",
                        value: "Produce & condiments: Lettuce, Peppers, Mayo, Oil Dressing, Pizza Sauce"
                    },
                    {
                        label: "Audit method",
                        value: "Weigh 5 samples per item against recipe card standard"
                    },
                    {
                        label: "Output",
                        value: "Variance log submitted to manager for period-over-period COGS tracking"
                    }
                ],
                followUp:
                    "Audit complete? Log the variance findings and I'll update the recovery estimate for next period.",
                quickReplies: [
                    "Log variance findings",
                    "All portions matched",
                    "Schedule recurring audit",
                    "Explain recovery impact"
                ]
            }
        }
        /* Trimmed to keep the actions list to 3 — restore if needed.
        {
            id: "act_cogs_track_recovery",
            kind: "cogs",
            category: "COGS",
            title: "Track recovery progress per item over time",
            description:
                "After an intervention is logged, automatically track whether the targeted item's variance dollar amount decreases in the following period — attributing measured recovery to the specific action taken and updating the running annual estimate.",
            ctaLabel: "Track Progress",
            workflow: {
                intro: "Here's how I'll track recovery progress per item:",
                bullets: [
                    {
                        label: "Trigger",
                        value: "An intervention is logged against an item"
                    },
                    {
                        label: "Measure",
                        value: "Change in that item's variance dollars the next period"
                    },
                    {
                        label: "Attribute",
                        value: "Tie measured recovery to the specific action taken"
                    },
                    {
                        label: "Update",
                        value: "Running annual recovery estimate adjusts automatically"
                    }
                ],
                followUp:
                    "Want a single recovery scorecard across all items, or a per-item view so you can see which fixes stuck?",
                quickReplies: [
                    "Show a scorecard",
                    "Per-item view",
                    "Flag fixes that regressed",
                    "Explain attribution"
                ]
            }
        }
        */
    ]
};

export const laborDinnerOvertimeCard: InsightCard = {
    id: "ins_labor_dinner_overtime",
    source: "PAR Operations · Labor Benchmark",
    confidence: 87,
    headlineMetric: {
        value: "$840",
        label: "Dinner over benchmark, Mon–Wed",
        direction: "negative"
    },
    title: "Dinner Over Benchmark — Overtime + Late Closers",
    tags: ["11 OT Hours Thu–Sat", "Closers +45 Min", "Lunch Is a Bright Spot"],
    narrative:
        "Dinner shifts Mon–Wed ran ~$840 over benchmark. Two causes: 11 hours of overtime already scheduled Thu–Sat, and closers stayed ~45 minutes past demand drop-off three nights in a row. Lunch is actually a bright spot — below benchmark spend, above benchmark sales per labor hour.",
    aiNote: "The overtime is still scheduled, not yet worked — trimming it Thu–Sat is the one move that recovers most of the dinner gap before it's spent.",
    actions: [
        {
            id: "act_labor_trim_overtime",
            kind: "schedule",
            category: "Schedule",
            title: "Trim scheduled overtime for Thu–Sat",
            description:
                "Surface the 11 overtime hours by employee and shift directly in the schedule view, with a one-click option to adjust clock-out times or swap to a non-overtime employee — showing the projected dollar save before the manager confirms.",
            ctaLabel: "Adjust Schedule",
            workflow: {
                intro: "Here's how I'll surface the overtime to trim:",
                bullets: [
                    {
                        label: "Breakdown",
                        value: "All 11 OT hours by employee and shift, Thu–Sat"
                    },
                    {
                        label: "One-click fix",
                        value: "Adjust clock-out time or swap to a non-overtime employee"
                    },
                    {
                        label: "Preview",
                        value: "Projected dollar save shown before the manager confirms"
                    },
                    {
                        label: "Guardrail",
                        value: "Flags any shift that would drop below minimum coverage"
                    }
                ],
                followUp:
                    "Want me to auto-suggest the lowest-impact swaps, or just list the OT hours for the manager to decide?",
                quickReplies: [
                    "Auto-suggest swaps",
                    "Just list the hours",
                    "Protect coverage minimums",
                    "Show the dollar save"
                ]
            }
        }
    ]
};

export const laborLiveStaffingCard: InsightCard = {
    id: "ins_labor_live_staffing",
    source: "PAR Operations · Live Labor",
    confidence: 84,
    headlineMetric: {
        value: "~$95",
        label: "Immediate overstaffed save available",
        direction: "positive"
    },
    title: "Two Live Scenarios — Over- and Understaffed",
    tags: [
        "Overstaffed: 5 vs 3",
        "Understaffed: Rush Incoming",
        "OT Exposure Tonight"
    ],
    narrative:
        "Two live scenarios: overstaffed — sales 22% below forecast, 5 people clocked in vs. 3 needed, sending one home saves ~$95 and avoids overtime exposure. Understaffed — sales 18% above forecast, 4 people on, incoming rush needs coverage before it peaks.",
    aiNote: "Both scenarios are time-sensitive — the overstaffed save shrinks every minute, and the understaffed rush needs coverage before it peaks, not after.",
    actions: [
        {
            id: "act_labor_send_home",
            kind: "schedule",
            category: "Schedule",
            title: "Suggest who to send home (overstaffed)",
            description:
                "When overstaffed, surface a ranked list of who to cut first — factoring in who's closest to overtime, who's been on longest this shift, and who has the lowest-impact role for the current hour — so the manager makes the call in under 30 seconds.",
            ctaLabel: "Suggest Cut",
            workflow: {
                intro: "Here's how I'll rank who to send home:",
                bullets: [
                    {
                        label: "Factors",
                        value: "Closeness to overtime, hours on this shift, role impact this hour"
                    },
                    {
                        label: "Output",
                        value: "A ranked cut list with the projected save per person"
                    },
                    {
                        label: "Speed",
                        value: "Designed for a sub-30-second decision"
                    },
                    {
                        label: "Guardrail",
                        value: "Never recommends dropping below needed coverage"
                    }
                ],
                followUp:
                    "Want me to weight overtime avoidance highest, or balance it against role coverage for the current hour?",
                quickReplies: [
                    "Prioritize OT avoidance",
                    "Balance coverage",
                    "Show the save per person",
                    "Pick the top one"
                ]
            }
        },
        {
            id: "act_labor_on_call",
            kind: "schedule",
            category: "Schedule",
            title: "Show on-call list for immediate call-in (understaffed)",
            description:
                "When understaffed, surface the on-call roster sorted by availability, proximity to the store, and hours worked this week — with a one-tap message to the top candidate that includes the shift window and expected sales pace.",
            ctaLabel: "Show On-Call",
            workflow: {
                intro: "Here's the on-call list I'll surface:",
                bullets: [
                    {
                        label: "Sort by",
                        value: "Availability, proximity to the store, hours worked this week"
                    },
                    {
                        label: "One-tap",
                        value: "Pre-filled message to the top candidate"
                    },
                    {
                        label: "Includes",
                        value: "Shift window and expected sales pace"
                    },
                    {
                        label: "Avoids",
                        value: "Anyone who'd be pushed into overtime by coming in"
                    }
                ],
                followUp:
                    "Want me to message just the top candidate, or send to the top 3 and take whoever confirms first?",
                quickReplies: [
                    "Top candidate only",
                    "Message top 3",
                    "Exclude OT risk",
                    "Edit the message"
                ]
            }
        },
        {
            id: "act_labor_live_impact",
            kind: "labor",
            category: "Labor",
            title: "Show live dollar impact of acting now vs. waiting",
            description:
                "Display a live counter showing the cost of the current overstaffing per minute — and the projected end-of-shift savings if the manager acts now vs. in 30 minutes. Making the cost visible in real time creates urgency without requiring a manual calculation.",
            ctaLabel: "Show Impact",
            workflow: {
                intro: "Here's the live impact counter I'll show:",
                bullets: [
                    {
                        label: "Live counter",
                        value: "Cost of current overstaffing, per minute"
                    },
                    {
                        label: "Compare",
                        value: "Projected end-of-shift save: act now vs. in 30 minutes"
                    },
                    {
                        label: "Effect",
                        value: "Makes the cost of waiting visible in real time"
                    },
                    {
                        label: "No math",
                        value: "Manager sees the number without a manual calculation"
                    }
                ],
                followUp:
                    "Want the counter pinned to the top of the schedule view, or only shown when overstaffing crosses a threshold?",
                quickReplies: [
                    "Pin it to the top",
                    "Only above a threshold",
                    "Show shift total",
                    "Explain the math"
                ]
            }
        }
        /* Trimmed to keep the actions list to 3 — restore if needed.
        {
            id: "act_labor_ot_alert_tonight",
            kind: "alert",
            category: "Alert",
            title: "Set an overtime exposure alert for tonight",
            description:
                "If any clocked-in employee is within 1.5 hours of hitting overtime, surface a proactive alert with the option to note a planned early cut — preventing overtime exposure from a slow-day staffing decision compounding into next week's labor cost.",
            ctaLabel: "Set Alert",
            workflow: {
                intro: "Here's the overtime exposure alert for tonight:",
                bullets: [
                    {
                        label: "Trigger",
                        value: "Any clocked-in employee within 1.5 hours of overtime"
                    },
                    {
                        label: "Option",
                        value: "Note a planned early cut directly from the alert"
                    },
                    {
                        label: "Prevents",
                        value: "A slow-day decision compounding into next week's cost"
                    },
                    {
                        label: "Scope",
                        value: "Tonight's shift, all clocked-in employees"
                    }
                ],
                followUp:
                    "Want the 1.5-hour warning window, or a tighter 1-hour window so cuts can be planned sooner?",
                quickReplies: [
                    "Use 1.5 hours",
                    "Tighten to 1 hour",
                    "Notify the manager",
                    "Make it recurring"
                ]
            }
        },
        {
            id: "act_labor_forecast_miss",
            kind: "ai_task",
            category: "AI Task",
            title: "Identify what caused today's forecast miss",
            description:
                "After the shift, run an AI task to surface why today's sales diverged from forecast — weather, a local event, a nearby competitor offer, or a day-of-week anomaly — so future forecasts can be adjusted and the manager isn't caught off guard again.",
            ctaLabel: "Run Analysis",
            workflow: {
                intro: "Here's the forecast-miss analysis I'll run:",
                bullets: [
                    {
                        label: "Check",
                        value: "Weather, local events, competitor promos, day-of-week anomalies"
                    },
                    {
                        label: "Compare",
                        value: "Today's actual sales pace vs. the forecast curve"
                    },
                    {
                        label: "Output",
                        value: "The most likely cause, with supporting signal"
                    },
                    {
                        label: "Goal",
                        value: "Feed the finding back so future forecasts adjust"
                    }
                ],
                followUp:
                    "Want me to automatically apply the finding to tomorrow's forecast, or just report the cause for review?",
                quickReplies: [
                    "Apply to tomorrow",
                    "Report only",
                    "Check the whole week",
                    "Explain the signal"
                ]
            }
        }
        */
    ]
};

export const laborStoreOutliersCard: InsightCard = {
    id: "ins_labor_store_outliers",
    source: "PAR Operations · Labor Outliers",
    confidence: 89,
    headlineMetric: {
        value: "8 stores",
        label: "Over labor this week",
        direction: "negative"
    },
    title: "8 Stores Over — A Best-Store Playbook Exists",
    tags: ["4471: 38 OT Hours", "2210: ~50 Min Tail", "Best Store 1188 Runs Lean"],
    narrative:
        "8 stores over this week. Store 4471: 38 OT hours, closing crew issue. Store 2210: ~50 min avg delay between slowdown and last clock-out. Store 3905: flat staffing despite a clear sales peak. Best store (1188) runs lean via disciplined cut schedules and near-zero overtime — the playbook exists.",
    aiNote: "Each outlier has a different primary driver, so a single generic message won't land — store-specific coaching tied to each driver is what moves the numbers.",
    actions: [
        {
            id: "act_labor_coaching_scripts",
            kind: "coaching",
            category: "Coaching",
            title: "Generate per-store coaching scripts",
            description:
                "For each underperforming store, auto-generate a 3–4 sentence coaching prompt tailored to their specific driver — overtime concentration for 4471, cut-time discipline for 2210, daypart scheduling for 3905 — so the RM walks in knowing exactly what to say.",
            ctaLabel: "Generate Scripts",
            workflow: {
                intro: "Here's how I'll generate the per-store coaching scripts:",
                bullets: [
                    {
                        label: "Per store",
                        value: "A 3–4 sentence prompt tied to that store's primary driver"
                    },
                    {
                        label: "Examples",
                        value: "OT concentration (4471), cut-time discipline (2210), daypart (3905)"
                    },
                    {
                        label: "Tone",
                        value: "Specific and actionable, not generic advice"
                    },
                    {
                        label: "For",
                        value: "The RM to use directly in the conversation"
                    }
                ],
                followUp:
                    "Want scripts for all 8 stores, or just the top 3 offenders to keep the RM's week focused?",
                quickReplies: [
                    "All 8 stores",
                    "Top 3 only",
                    "Add the data points",
                    "Adjust the tone"
                ]
            }
        },
        {
            id: "act_labor_coaching_visits",
            kind: "coaching",
            category: "Coaching",
            title: "Schedule coaching visits for top offenders",
            description:
                "Create calendar tasks for the RM to visit or call the top 3 stores this week — pre-populated with the store's specific issue, the recommended conversation agenda, and a follow-up check-in date to verify improvement in next week's numbers.",
            ctaLabel: "Schedule Visits",
            workflow: {
                intro: "Here's the coaching-visit schedule I'll create:",
                bullets: [
                    {
                        label: "Targets",
                        value: "The top 3 stores by labor gap this week"
                    },
                    {
                        label: "Each task",
                        value: "Store's issue, recommended agenda, suggested time"
                    },
                    {
                        label: "Follow-up",
                        value: "A check-in date to verify next week's improvement"
                    },
                    {
                        label: "Format",
                        value: "Calendar tasks on the RM's schedule"
                    }
                ],
                followUp:
                    "Want visits for the top 3, or include phone check-ins for the rest of the outlier list?",
                quickReplies: [
                    "Top 3 visits",
                    "Add phone check-ins",
                    "Set the follow-up dates",
                    "Assign to the RM"
                ]
            }
        },
        {
            id: "act_labor_outlier_digest",
            kind: "comms",
            category: "Slack / Email",
            title: "Send weekly outlier digest to RM",
            description:
                "Auto-deliver a Monday morning Slack or email summary: stores ranked by labor gap vs. best-in-cohort, the top driver per store, and a suggested coaching priority for the week — so the RM starts each week with a clear, data-backed plan of action.",
            ctaLabel: "Send Digest",
            workflow: {
                intro: "Here's the weekly outlier digest I'll send:",
                bullets: [
                    {
                        label: "Ranking",
                        value: "Stores by labor gap vs. best-in-cohort"
                    },
                    {
                        label: "Per store",
                        value: "The top driver and a suggested coaching priority"
                    },
                    {
                        label: "Cadence",
                        value: "Monday morning, Slack or email"
                    },
                    {
                        label: "Goal",
                        value: "RM starts the week with a data-backed plan"
                    }
                ],
                followUp:
                    "Want it via Slack or email, and should I include the full list or just stores that are over?",
                quickReplies: [
                    "Send via Slack",
                    "Send via email",
                    "Only stores over",
                    "Include the full list"
                ]
            }
        }
        /* Trimmed to keep the actions list to 3 — restore if needed.
        {
            id: "act_labor_track_improvement",
            kind: "labor",
            category: "Labor",
            title: "Track store improvement after coaching",
            description:
                "After a coaching visit is logged, automatically track whether the flagged store's primary driver (OT hours, cut-time delay, daypart gap) improves in the following 1–2 week periods — attributing improvement to the intervention and surfacing stores that aren't responding.",
            ctaLabel: "Track Improvement",
            workflow: {
                intro: "Here's how I'll track improvement after coaching:",
                bullets: [
                    {
                        label: "Trigger",
                        value: "A coaching visit is logged for a store"
                    },
                    {
                        label: "Measure",
                        value: "That store's primary driver over the next 1–2 weeks"
                    },
                    {
                        label: "Attribute",
                        value: "Tie any improvement back to the intervention"
                    },
                    {
                        label: "Surface",
                        value: "Stores that aren't responding, for follow-up"
                    }
                ],
                followUp:
                    "Want a 1-week or 2-week measurement window before flagging a store as non-responsive?",
                quickReplies: [
                    "Use 1 week",
                    "Use 2 weeks",
                    "Show responders too",
                    "Explain attribution"
                ]
            }
        },
        {
            id: "act_labor_consecutive_weeks_alert",
            kind: "alert",
            category: "Alert",
            title: "Flag stores with multiple consecutive over-weeks",
            description:
                "Create an escalation rule: if a store appears in the outlier list for 3+ consecutive weeks without improvement in their primary driver, escalate a notification to the DM or above — distinguishing a coaching opportunity from a structural problem that needs more than a script.",
            ctaLabel: "Set Escalation",
            workflow: {
                intro: "Here's the consecutive-over-weeks escalation rule:",
                bullets: [
                    {
                        label: "Trigger",
                        value: "3+ consecutive weeks on the outlier list, no improvement"
                    },
                    {
                        label: "Escalate to",
                        value: "The DM or above"
                    },
                    {
                        label: "Distinguishes",
                        value: "A coaching opportunity from a structural problem"
                    },
                    {
                        label: "Tracks",
                        value: "Improvement in the store's primary driver, not just totals"
                    }
                ],
                followUp:
                    "Want the 3-week threshold, or escalate sooner at 2 weeks for stores with the largest gaps?",
                quickReplies: [
                    "Use 3 weeks",
                    "Escalate at 2 weeks",
                    "Notify the DM",
                    "Explain the rule"
                ]
            }
        }
        */
    ]
};

export const sosPeakSpikeCard: InsightCard = {
    id: "ins_sos_peak_spike",
    source: "PAR Operations · Drive-Thru SOS",
    confidence: 90,
    headlineMetric: {
        value: "21:41",
        label: "Peak drive-thru SOS, 6–7 PM yesterday",
        direction: "negative"
    },
    title: "Dinner Rush SOS Spike — 6–7 PM",
    tags: ["6–7 PM Window", "11:23 Avg", "21:41 Peak"],
    narrative:
        "Yesterday between 6 and 7 PM, average drive-thru speed of service was 11:23 — with a peak SOS of 21:41 on a single car. That's roughly 3× a healthy dinner target and well above the rest of the day. A spike this sharp inside one daypart almost always traces back to a specific event, not steady-state slowness.",
    aiNote: "A single 21:41 car pulling the hour's average to 11:23 points to a cascade — one stalled order backing up the lane — rather than uniformly slow service. Pinpointing the trigger car is the fastest path to preventing a repeat.",
    actions: [
        {
            id: "act_sos_peak_assign_mod",
            kind: "ops_task",
            category: "Ops Task",
            title: "Assign to manager on duty for review",
            description:
                "Route the 6–7 PM SOS spike to yesterday's manager on duty with the hour's transaction timeline attached — so they can review what happened during the window and confirm whether it was a one-off or an emerging pattern.",
            ctaLabel: "Assign for Review",
            workflow: {
                intro: "Here's the review task I'll assign to the manager on duty:",
                bullets: [
                    {
                        label: "Window",
                        value: "Yesterday 6–7 PM, drive-thru lane"
                    },
                    {
                        label: "Attached",
                        value: "Transaction timeline with the 21:41 peak car flagged"
                    },
                    {
                        label: "Ask",
                        value: "Confirm whether it was a one-off event or a recurring pattern"
                    },
                    {
                        label: "Due",
                        value: "Before tonight's dinner rush, so any fix applies today"
                    }
                ],
                followUp:
                    "Want me to route this to the MOD who was on yesterday, or to tonight's MOD so they can watch for a repeat?",
                quickReplies: [
                    "Assign to yesterday's MOD",
                    "Assign to tonight's MOD",
                    "Add the timeline",
                    "Set the due time"
                ]
            }
        },
        {
            id: "act_sos_peak_root_cause",
            kind: "ai_task",
            category: "AI Task",
            title: "Run root cause analysis on the spike",
            description:
                "Trigger an AI task to diagnose the 6–7 PM spike: check for a cascade car that stalled the lane, a cluster of large or high-complexity orders, a gap in manager-on-duty coverage, kitchen ticket-time backups, or a staffing dip — and rank the most likely cause with supporting evidence.",
            ctaLabel: "Run Analysis",
            workflow: {
                intro: "Here's how I'll run the root cause analysis on the spike:",
                bullets: [
                    {
                        label: "Cascade check",
                        value: "Find a single stalled car that backed up the lane behind it"
                    },
                    {
                        label: "Order mix",
                        value: "Cluster of large orders or high menu complexity in the window"
                    },
                    {
                        label: "Coverage",
                        value: "Manager-on-duty presence and staffing level for the hour"
                    },
                    {
                        label: "Output",
                        value: "Ranked likely causes with the supporting transaction evidence"
                    }
                ],
                followUp:
                    "Want me to focus on just the single worst car, or analyze the whole 6–7 PM window for compounding factors?",
                quickReplies: [
                    "Focus on the peak car",
                    "Analyze the full hour",
                    "Check kitchen ticket times",
                    "Compare to a normal night"
                ]
            }
        }
    ]
};

export const sosTrendSlowingCard: InsightCard = {
    id: "ins_sos_trend_slowing",
    source: "PAR Operations · Drive-Thru SOS",
    confidence: 88,
    headlineMetric: {
        value: "3:12 → 3:44",
        label: "Average SOS, slower over 2 weeks",
        direction: "negative"
    },
    title: "SOS Trending Slower — 2-Week Drift",
    tags: ["+32 sec / Car", "~17% Slower", "2-Week Trend"],
    narrative:
        "Average drive-thru speed of service has drifted from 3:12 to 3:44 over the past two weeks — about 32 seconds slower per car, a ~17% increase. A steady creep like this isn't a one-night event; it usually reflects a structural change in staffing, order mix, or demand that's quietly compounding shift over shift.",
    aiNote: "A gradual 2-week slide rarely comes from a single bad night — it's the signature of a slow-moving driver like chronic understaffing or rising order complexity. Identifying which one is trending lets you correct it before it becomes the new normal.",
    actions: [
        {
            id: "act_sos_trend_assign_mod",
            kind: "ops_task",
            category: "Ops Task",
            title: "Assign to manager on duty for review",
            description:
                "Route the 2-week SOS trend to the manager on duty with the daily averages charted — so they can review the slide against staffing and volume for the same period and confirm what changed two weeks ago.",
            ctaLabel: "Assign for Review",
            workflow: {
                intro: "Here's the trend review I'll assign to the manager on duty:",
                bullets: [
                    {
                        label: "Trend",
                        value: "Daily average SOS over the last 2 weeks, 3:12 → 3:44"
                    },
                    {
                        label: "Overlay",
                        value: "Staffing and car-count for the same period"
                    },
                    {
                        label: "Ask",
                        value: "Identify what changed ~2 weeks ago when the drift started"
                    },
                    {
                        label: "Goal",
                        value: "Pinpoint a correctable cause before it sets in"
                    }
                ],
                followUp:
                    "Want this assigned to the GM for a structural look, or to the shift MOD who owns the dinner daypart where most of the drift shows?",
                quickReplies: [
                    "Assign to the GM",
                    "Assign to the MOD",
                    "Add the trend chart",
                    "Set a follow-up"
                ]
            }
        },
        {
            id: "act_sos_trend_root_cause",
            kind: "ai_task",
            category: "AI Task",
            title: "Run root cause analysis on the trend",
            description:
                "Trigger an AI task to explain the 2-week slowdown: test for chronic understaffing during peak dayparts, a rising average order complexity or size, an increase in car-count/demand, new-hire ramp, or a menu change — and rank which factor best correlates with the timing of the drift.",
            ctaLabel: "Run Analysis",
            workflow: {
                intro: "Here's how I'll run the root cause analysis on the trend:",
                bullets: [
                    {
                        label: "Staffing",
                        value: "Test for chronic understaffing during the dayparts that slowed"
                    },
                    {
                        label: "Order mix",
                        value: "Check for rising average order size or menu complexity"
                    },
                    {
                        label: "Demand",
                        value: "Look for an increase in car-count over the 2 weeks"
                    },
                    {
                        label: "Output",
                        value: "Ranked factors by how well each lines up with the drift timing"
                    }
                ],
                followUp:
                    "Should I weight each factor by how closely it tracks the day-by-day slowdown, or just report the single strongest driver?",
                quickReplies: [
                    "Weight by timing",
                    "Single strongest driver",
                    "Break out by daypart",
                    "Compare to last month"
                ]
            }
        }
    ]
};

export const sosThursdayStaffingCard: InsightCard = {
    id: "ins_sos_thursday_staffing",
    source: "PAR Operations · Drive-Thru SOS",
    confidence: 87,
    headlineMetric: {
        value: "Thu 5–8 PM",
        label: "Predictably understaffed — SOS suffers",
        direction: "negative"
    },
    title: "Predicted: Thursday Dinner Understaffing",
    tags: ["4 of Last 5 Thursdays", "+1:10 vs Other Nights", "Add 1 to Schedule"],
    narrative:
        "Across the last several weeks, Thursday evenings show a repeating pattern: drive-thru SOS runs about 1:10 slower than comparable nights, every Thursday 5–8 PM. It lines up with a recurring staffing gap — Thursday dinner is scheduled one person lighter than Friday and Saturday despite similar car-counts. This Thursday is tracking the same way, so the slowdown is predictable and preventable.",
    aiNote: "This isn't a one-off — it's a recurring Thursday pattern driven by a structural schedule gap. Adding one person to Thursday 5–8 PM before the rush, rather than reacting during it, is the highest-leverage fix.",
    actions: [
        {
            id: "act_sos_thursday_add_staff",
            kind: "schedule",
            category: "Schedule",
            title: "Add an employee to Thursday dinner",
            description:
                "Surface the Thursday 5–8 PM coverage gap against forecasted car-count and recommend adding one person to the schedule — showing available employees who aren't near overtime and the projected SOS improvement based on prior weeks where coverage was fuller.",
            ctaLabel: "Adjust Schedule",
            workflow: {
                intro: "Here's the Thursday dinner staffing recommendation:",
                bullets: [
                    {
                        label: "Window",
                        value: "Thursday 5–8 PM, drive-thru — the recurring slow spot"
                    },
                    {
                        label: "Gap",
                        value: "Scheduled one person lighter than Fri/Sat at similar car-counts"
                    },
                    {
                        label: "Suggest",
                        value: "Available employees not near overtime, ranked by fit"
                    },
                    {
                        label: "Projected",
                        value: "Expected SOS improvement based on fuller-coverage weeks"
                    }
                ],
                followUp:
                    "Want me to add coverage just this Thursday, or make it a standing schedule change for every Thursday dinner?",
                quickReplies: [
                    "Just this Thursday",
                    "Make it recurring",
                    "Show available staff",
                    "Project the SOS gain"
                ]
            }
        },
        {
            id: "act_sos_thursday_confirm_pattern",
            kind: "ai_task",
            category: "AI Task",
            title: "Confirm the Thursday pattern before acting",
            description:
                "Trigger an AI task to validate the prediction: compare the last 6–8 Thursdays' SOS and staffing against other weeknights, control for car-count and weather, and quantify how much of the Thursday slowdown is explained by the staffing gap versus other factors.",
            ctaLabel: "Run Analysis",
            workflow: {
                intro: "Here's how I'll confirm the Thursday understaffing pattern:",
                bullets: [
                    {
                        label: "Compare",
                        value: "Last 6–8 Thursdays' SOS and staffing vs. other weeknights"
                    },
                    {
                        label: "Control for",
                        value: "Car-count and weather, so it's a fair comparison"
                    },
                    {
                        label: "Quantify",
                        value: "Share of the slowdown explained by the staffing gap"
                    },
                    {
                        label: "Output",
                        value: "A confidence level on the prediction before you change the schedule"
                    }
                ],
                followUp:
                    "Want me to also check whether the same gap shows up on any other weeknight, or keep it focused on Thursdays?",
                quickReplies: [
                    "Thursdays only",
                    "Check all weeknights",
                    "Add the car-count overlay",
                    "Explain the method"
                ]
            }
        }
    ]
};

export const sosLunchDaypartCard: InsightCard = {
    id: "ins_sos_lunch_daypart",
    source: "PAR Operations · Drive-Thru SOS",
    confidence: 85,
    headlineMetric: {
        value: "Lunch",
        label: "Daypart trending slower — 2 weeks",
        direction: "negative"
    },
    title: "Daypart Breakdown — Lunch Is the Slow Spot",
    tags: ["Lunch: 3:38 → 4:09", "Dinner Steady", "Breakfast On Target"],
    narrative:
        "Breaking SOS down by daypart, lunch is where the slowdown lives: the 11 AM–2 PM average has slipped from 3:38 to 4:09 over the past two weeks, while breakfast and dinner held steady. Whatever is driving the overall slowdown is concentrated in the midday rush, not spread across the day — which makes it a narrower, more fixable problem.",
    aiNote: "When the drift is isolated to one daypart, the cause usually lives there too — a midday staffing change, a lunch-specific menu or order-size shift, or a new nearby lunch competitor. Diagnosing lunch alone is far cheaper than chasing an all-day fix.",
    actions: [
        {
            id: "act_sos_lunch_root_cause",
            kind: "ai_task",
            category: "AI Task",
            title: "Diagnose what's slowing lunch",
            description:
                "Trigger an AI task scoped to the 11 AM–2 PM daypart: test for a midday staffing reduction, a rise in average lunch order size or complexity, an increase in lunch car-count, and any recent menu or promo change — then rank which best explains the two-week slide.",
            ctaLabel: "Run Analysis",
            workflow: {
                intro: "Here's how I'll diagnose the lunch slowdown:",
                bullets: [
                    {
                        label: "Scope",
                        value: "11 AM–2 PM daypart only, last 2 weeks"
                    },
                    {
                        label: "Staffing",
                        value: "Check for a reduction in midday coverage"
                    },
                    {
                        label: "Order mix",
                        value: "Rising lunch order size, complexity, or car-count"
                    },
                    {
                        label: "Output",
                        value: "Ranked causes by how well each tracks the lunch drift"
                    }
                ],
                followUp:
                    "Want me to compare lunch against your best-performing store's lunch, or just analyze this store's trend in isolation?",
                quickReplies: [
                    "Benchmark vs best store",
                    "This store only",
                    "Break out by weekday",
                    "Explain the method"
                ]
            }
        },
        {
            id: "act_sos_lunch_assign_mod",
            kind: "ops_task",
            category: "Ops Task",
            title: "Assign lunch review to manager on duty",
            description:
                "Route the lunch daypart slowdown to the midday manager on duty with the 2-week trend charted — so they can review the 11 AM–2 PM window against staffing and order mix and confirm what changed two weeks ago.",
            ctaLabel: "Assign for Review",
            workflow: {
                intro: "Here's the lunch review I'll assign to the manager on duty:",
                bullets: [
                    {
                        label: "Window",
                        value: "Lunch daypart, 11 AM–2 PM, last 2 weeks"
                    },
                    {
                        label: "Trend",
                        value: "Daily lunch SOS average, 3:38 → 4:09"
                    },
                    {
                        label: "Overlay",
                        value: "Midday staffing and car-count for the same period"
                    },
                    {
                        label: "Ask",
                        value: "Confirm what changed ~2 weeks ago in the lunch window"
                    }
                ],
                followUp:
                    "Want this assigned to the midday MOD, or to the GM since the trend spans multiple shifts?",
                quickReplies: [
                    "Assign to midday MOD",
                    "Assign to the GM",
                    "Add the trend chart",
                    "Set a follow-up"
                ]
            }
        }
    ]
};

export const sosStoreOutliersCard: InsightCard = {
    id: "ins_sos_store_outliers",
    source: "PAR Operations · Drive-Thru SOS",
    confidence: 89,
    headlineMetric: {
        value: "6 stores",
        label: "Over SOS benchmark this week",
        direction: "negative"
    },
    title: "6 Stores Over SOS — A Fast-Store Playbook Exists",
    tags: ["4471: 5:02 Avg", "Best Store 1188: 2:48", "Same Daypart Driver"],
    narrative:
        "6 stores are over the SOS benchmark this week. Store 4471 averages 5:02 — the worst — concentrated in the dinner daypart. The best store, 1188, holds 2:48 through the same rush with disciplined pull-forward timing and a second order-taker deployed before the peak. The gap is coachable: the fast-store playbook already exists in your own fleet.",
    aiNote: "Most of the over-benchmark stores share the same dinner-daypart driver, so a single playbook from store 1188 — second order-taker before peak, tight pull discipline — likely lifts several of them at once.",
    actions: [
        {
            id: "act_sos_outliers_coaching",
            kind: "coaching",
            category: "Coaching",
            title: "Generate per-store SOS coaching scripts",
            description:
                "For each over-benchmark store, auto-generate a short coaching prompt tied to its specific SOS driver — dinner-rush staffing for 4471, pull-forward timing for the park-out stores — built from what the fastest store in the cohort does differently.",
            ctaLabel: "Generate Scripts",
            workflow: {
                intro: "Here's how I'll generate the per-store SOS coaching scripts:",
                bullets: [
                    {
                        label: "Per store",
                        value: "A short prompt tied to that store's SOS driver"
                    },
                    {
                        label: "Benchmark",
                        value: "Built from what store 1188 (2:48) does differently"
                    },
                    {
                        label: "Examples",
                        value: "Dinner staffing (4471), pull-forward timing (park-out stores)"
                    },
                    {
                        label: "For",
                        value: "The RM to use directly with each store manager"
                    }
                ],
                followUp:
                    "Want scripts for all 6 stores, or just the top 3 worst offenders to keep the RM focused?",
                quickReplies: [
                    "All 6 stores",
                    "Top 3 only",
                    "Add the SOS data",
                    "Adjust the tone"
                ]
            }
        },
        {
            id: "act_sos_outliers_alert",
            kind: "alert",
            category: "Alert",
            title: "Alert when a store crosses the SOS benchmark",
            description:
                "Set a rule that fires when any store's daily average SOS crosses the benchmark threshold for two days running — routing a notification to the RM with the store, the daypart driving it, and how far over the cohort it is — so drift is caught before it becomes a weekly pattern.",
            ctaLabel: "Set Alert",
            workflow: {
                intro: "Here's the SOS benchmark alert I'll set:",
                bullets: [
                    {
                        label: "Trigger",
                        value: "Daily average SOS over benchmark for two days running"
                    },
                    {
                        label: "Includes",
                        value: "Store, the daypart driving it, and the gap vs. cohort"
                    },
                    {
                        label: "Routed to",
                        value: "The RM, before it becomes a weekly pattern"
                    },
                    {
                        label: "Scope",
                        value: "All stores in the cohort"
                    }
                ],
                followUp:
                    "Want the two-day trigger, or a tighter single-day alert for stores already on the watch list?",
                quickReplies: [
                    "Use two days",
                    "Single day for watch list",
                    "Notify the RM",
                    "Explain the rule"
                ]
            }
        }
    ]
};

export const demoPromotionEffectivenessCard: InsightCard = {
    id: "ins_demo_promotion_effectiveness",
    source: "PAR Punchh® · Offer Effectiveness",
    confidence: 86,
    headlineMetric: {
        value: "$4,200/wk",
        label: "Margin recoverable from over-discounting",
        direction: "positive"
    },
    tracker: {
        metricLabel: "Redemption rate",
        actual: "4.2%",
        goal: "7.0%"
    },
    title: "Offer Effectiveness Advisor",
    tags: ["BOGO Chicken Sandwich", "4.2% Redemption vs 7%", "61% Loyalty Members"],
    narrative:
        "Your Buy One Get One chicken sandwich offer has been running for 3 weeks across all locations. Redemption rate is 4.2% — below the 7% threshold that typically signals a strong offer. Traffic is up 8% during offer windows, but 61% of redemptions are coming from existing loyalty members who would have visited anyway.",
    aiNote: "You're discounting your most loyal customers without driving incremental visits. Narrowing the offer to non-member guests, or capping it to first-time redeemers, would preserve the traffic lift while recovering roughly $4,200/week in margin currently being given away to guests who don't need the incentive.",
    actions: [
        {
            id: "act_demo_promo_segment",
            kind: "campaign",
            category: "Update Segment",
            title: "Update campaign to target a new segment",
            description:
                "Retarget the BOGO chicken sandwich campaign to a higher-intent segment so the discount drives net-new visits instead of rewarding loyalty members who would have come anyway.",
            ctaLabel: "Update Segment",
            workflow: {
                intro: "Here's the segment change for the BOGO chicken sandwich campaign:",
                bullets: [
                    {
                        label: "Current",
                        value: "All guests — 61% of redemptions are existing loyalty members"
                    },
                    {
                        label: "Retarget to",
                        value: "A narrower, higher-intent segment, preserving the 8% traffic lift"
                    },
                    {
                        label: "Recovers",
                        value: "~$4,200/week in margin given to guests who'd visit anyway"
                    },
                    {
                        label: "Confirm",
                        value: "You'll review the full campaign and pick the segment before it's sent"
                    }
                ],
                followUp:
                    "Want to pick the segment now, or review the current campaign details first?",
                quickReplies: [
                    "Pick the segment",
                    "Review current campaign",
                    "Preview the impact",
                    "Target recent redeemers"
                ]
            }
        },
        {
            id: "act_demo_promo_promotion",
            kind: "campaign",
            category: "Update Offer",
            title: "Update campaign to use a different offer",
            description:
                "Swap the BOGO chicken sandwich gift type for one with a stronger draw so the same audience has a reason to redeem and the rate lifts above the 7% threshold.",
            ctaLabel: "Update Offer",
            workflow: {
                intro: "Here's the offer swap for the campaign:",
                bullets: [
                    {
                        label: "Current",
                        value: "BOGO chicken sandwich — 4.2% redemption vs. the 7% threshold"
                    },
                    {
                        label: "Swap to",
                        value: "A gift type with a stronger draw than the current one"
                    },
                    {
                        label: "Goal",
                        value: "Give the same audience a reason to redeem and lift the rate"
                    },
                    {
                        label: "Confirm",
                        value: "You'll review the full campaign and pick the gift type before it's sent"
                    }
                ],
                followUp:
                    "Want to pick the new gift type now, or review the current campaign details first?",
                quickReplies: [
                    "Pick the gift type",
                    "Review current campaign",
                    "Preview the impact",
                    "Compare gift types"
                ]
            }
        },
        {
            id: "act_demo_promo_forecast",
            kind: "analysis",
            category: "Forecast",
            title: "Forecast results for a new offer scenario",
            description:
                "Model the expected redemption rate, traffic lift, and margin for a narrower-audience version of the offer before you commit to the change.",
            ctaLabel: "Run Forecast",
            workflow: {
                intro: "Here's how I'll forecast the reshaped offer:",
                bullets: [
                    {
                        label: "Inputs",
                        value: "3 weeks of BOGO redemption, traffic, and member-mix data"
                    },
                    {
                        label: "Scenario",
                        value: "Narrower, higher-intent audience version of the offer"
                    },
                    {
                        label: "Projects",
                        value: "Redemption rate, incremental visits, and weekly margin recovered"
                    },
                    {
                        label: "Output",
                        value: "Side-by-side forecast vs. the current offer"
                    }
                ],
                followUp:
                    "Want a single forecast, or a side-by-side against the current offer?",
                quickReplies: [
                    "Side-by-side forecast",
                    "Single forecast only",
                    "Add a confidence range",
                    "Explain the method"
                ]
            }
        },
        {
            id: "act_demo_promo_delete",
            kind: "settings",
            category: "Delete Campaign",
            title: "Delete this campaign",
            description:
                "Delete the under-performing BOGO chicken sandwich campaign to immediately stop the margin leakage from low-rate sends to guests who would have visited anyway.",
            ctaLabel: "Delete Campaign",
            workflow: {
                intro: "Here's what deleting the current campaign does:",
                bullets: [
                    {
                        label: "Action",
                        value: "Remove the all-guest BOGO across all 18 locations"
                    },
                    {
                        label: "Effect",
                        value: "Stops ~$4,200/week in margin currently given to existing members"
                    },
                    {
                        label: "Confirm",
                        value: "You'll review the full campaign details before the delete is sent"
                    }
                ],
                followUp:
                    "Want to delete it outright, or retarget the segment instead?",
                quickReplies: [
                    "Delete it",
                    "Retarget instead",
                    "Review current campaign",
                    "Swap the offer"
                ]
            }
        }
    ],
    recommendedActionId: "act_demo_promo_promotion"
};

export const demoStoreHealthCard: InsightCard = {
    id: "ins_demo_store_health",
    source: "PAR Operations · Store Health Monitor",
    confidence: 88,
    headlineMetric: {
        value: "1.8%",
        label: "Void rate — 0.6 pts above location avg",
        direction: "negative"
    },
    tracker: {
        metricLabel: "Void rate",
        actual: "1.8%",
        goal: "1.2%"
    },
    title: "Store Health Monitor",
    tags: ["322 S Broadway", "Voids & Refunds Trending Up", "Manager Review Needed"],
    narrative:
        "Three things are trending in the wrong direction at 322 S Broadway this week. Your void rate is 1.8%, which is 0.6 points above your location average from the past 90 days. None of these are emergencies individually, but the combination suggests the store needs a manager-level review before they compound into a larger problem.",
    aiNote: "Against the trailing 90-day baseline, void rate, refund rate, and discount usage are all drifting up together at this one location. The pattern is early — catching it now with a manager review is far cheaper than waiting for it to show up in the P&L.",
    actions: [
        {
            id: "act_demo_health_review_log",
            kind: "report",
            category: "Review Log",
            title: "Review void and refund report",
            description:
                "Open the trailing void and refund report for 322 S Broadway to see which transactions, registers, and employees are driving the increase against the 90-day baseline.",
            ctaLabel: "Open Report",
            workflow: {
                intro: "Here's the void and refund report for 322 S Broadway:",
                bullets: [
                    {
                        label: "Scope",
                        value: "Voids and refunds this week vs. the trailing 90-day average"
                    },
                    {
                        label: "Breakdown",
                        value: "Grouped by register, employee, and reason code"
                    },
                    {
                        label: "Surfaces",
                        value: "The transactions and shifts driving the 0.6-point increase"
                    },
                    {
                        label: "Goal",
                        value: "Separate routine activity from anything that needs a closer look"
                    }
                ],
                followUp:
                    "Want me to flag any employee whose void rate is more than 2× the store average?",
                quickReplies: [
                    "Flag high-void employees",
                    "Group by reason code",
                    "Show just this week",
                    "Compare to brand benchmark"
                ]
            }
        },
        {
            id: "act_demo_health_assign_review",
            kind: "ops_task",
            category: "Assign Review",
            title: "Assign manager review task",
            description:
                "Create a manager-level review task for 322 S Broadway to investigate the void and refund trend and confirm whether it's a process, training, or staffing issue before it compounds.",
            ctaLabel: "Assign Review",
            workflow: {
                intro: "Here's the manager review task for 322 S Broadway:",
                bullets: [
                    {
                        label: "Location",
                        value: "322 S Broadway — void rate 1.8%, 0.6 pts above its 90-day average"
                    },
                    {
                        label: "Ask",
                        value: "Investigate the void and refund trend together"
                    },
                    {
                        label: "Outcome",
                        value: "Confirm root cause — process, training, or staffing — and log it"
                    },
                    {
                        label: "Due",
                        value: "Within the week, before the trend compounds"
                    }
                ],
                followUp:
                    "Want to assign this to the store GM, or route it to the district manager for a second set of eyes?",
                quickReplies: [
                    "Assign to store GM",
                    "Route to district manager",
                    "Set the due date",
                    "Attach the log"
                ]
            }
        },
        {
            id: "act_demo_health_threshold_alert",
            kind: "alert",
            category: "Alert",
            title: "Set custom alert threshold for a monitored KPI",
            description:
                "Configure a custom threshold so you're alerted whenever any monitored KPI — like void or refund rate — drifts a set number of points above a location's own 90-day average.",
            ctaLabel: "Configure Alert",
            workflow: {
                intro: "Here's the custom KPI threshold alert:",
                bullets: [
                    {
                        label: "Metric",
                        value: "Any monitored KPI — void rate or refund rate"
                    },
                    {
                        label: "Trigger",
                        value: "Drifts 0.5+ points above the location's own 90-day average"
                    },
                    {
                        label: "Scope",
                        value: "Per location, comparing each store to its own baseline"
                    },
                    {
                        label: "Routed to",
                        value: "Store manager and the regional manager"
                    }
                ],
                followUp:
                    "Want the 0.5-point threshold, or a tighter 0.3-point cutoff to catch drift even earlier?",
                quickReplies: [
                    "Use 0.5 points",
                    "Tighten to 0.3",
                    "Pick the KPIs",
                    "Choose recipients"
                ]
            }
        }
    ]
};

export const demoHighFoodCostCard: InsightCard = {
    id: "ins_demo_high_food_cost",
    source: "PAR Operations · Food Cost",
    confidence: 90,
    headlineMetric: {
        value: "$3,200",
        label: "Avoidable variance — 8.4% over theoretical",
        direction: "negative"
    },
    tracker: {
        metricLabel: "Avoidable variance",
        actual: "$3,200",
        goal: "$0"
    },
    title: "High Food Cost Issue",
    tags: [
        "322 S Broadway",
        "Classic Chicken Sandwich +28%",
        "1 Manager · Evening Shift"
    ],
    narrative:
        "Your food cost at 322 S Broadway is running 8.4% above theoretical over the past 14 days — $3,200 in avoidable variance. The single biggest driver is the Classic Chicken Sandwich: actual usage is running 28% above the recipe standard per serving. When we map that variance to shifts, 8 of the 12 highest-variance shifts share the same manager on duty — [Manager Name], evening shift, 4pm–close.",
    aiNote: "The pattern is tight enough to be meaningful: it's not a supply or receiving problem, it's a portioning consistency issue on one shift. A 30-minute re-training on that item alone would close the majority of the gap.",
    actions: [
        {
            id: "act_demo_foodcost_shift_breakdown",
            kind: "analysis",
            category: "Shift Breakdown",
            title: "View shift-level breakdown by manager",
            description:
                "Open the shift-level food-cost breakdown for 322 S Broadway, mapping each high-variance shift to the manager on duty so the portioning pattern is unmistakable.",
            ctaLabel: "View Breakdown",
            workflow: {
                intro: "Here's the shift-level breakdown mapped to manager on duty:",
                bullets: [
                    {
                        label: "Window",
                        value: "Last 14 days at 322 S Broadway"
                    },
                    {
                        label: "Mapping",
                        value: "Each shift's food-cost variance to the manager on duty"
                    },
                    {
                        label: "Pattern",
                        value: "8 of the 12 highest-variance shifts — evening, 4pm–close, same manager"
                    },
                    {
                        label: "Goal",
                        value: "Confirm it's a shift-level portioning issue, not store-wide"
                    }
                ],
                followUp:
                    "Want me to isolate the Classic Chicken Sandwich variance on just those evening shifts?",
                quickReplies: [
                    "Isolate the chicken sandwich",
                    "Show all managers",
                    "Compare day vs. evening",
                    "Export the breakdown"
                ]
            }
        },
        /* Trimmed to keep the actions list to 3 — restore if needed.
        {
            id: "act_demo_foodcost_item_variance",
            kind: "report",
            category: "Item Variance",
            title: "View item-level variance report",
            description:
                "Open the item-level variance report ranked by dollar impact, with the Classic Chicken Sandwich at the top showing actual usage 28% above the recipe standard.",
            ctaLabel: "View Report",
            workflow: {
                intro: "Here's the item-level variance report for 322 S Broadway:",
                bullets: [
                    {
                        label: "Ranked by",
                        value: "Dollar impact — actual usage vs. recipe standard"
                    },
                    {
                        label: "Top driver",
                        value: "Classic Chicken Sandwich — 28% above standard per serving"
                    },
                    {
                        label: "Window",
                        value: "Rolling 14 days, contributing to the $3,200 variance"
                    },
                    {
                        label: "Goal",
                        value: "Pinpoint exactly which items to retrain on first"
                    }
                ],
                followUp:
                    "Want the full ranked list, or just the top 3 items driving the dollar variance?",
                quickReplies: [
                    "Full ranked list",
                    "Top 3 items only",
                    "Add a recipe comparison",
                    "Export the report"
                ]
            }
        },
        */
        {
            id: "act_demo_foodcost_audit",
            kind: "checklist",
            category: "Assign Audit",
            title: "Assign portioning audit to location",
            description:
                "Create a portioning audit task for 322 S Broadway focused on the Classic Chicken Sandwich on the evening shift, to verify portion control against the recipe standard.",
            ctaLabel: "Assign Audit",
            workflow: {
                intro: "Here's the portioning audit task for 322 S Broadway:",
                bullets: [
                    {
                        label: "Focus",
                        value: "Classic Chicken Sandwich portion control vs. recipe standard"
                    },
                    {
                        label: "When",
                        value: "Evening shift, 4pm–close — where the variance concentrates"
                    },
                    {
                        label: "Verify",
                        value: "Portion weights, build process, and scale calibration"
                    },
                    {
                        label: "Outcome",
                        value: "Confirm root cause and log corrective steps"
                    }
                ],
                followUp:
                    "Want the audit scheduled for the next evening shift, or run it across a full week?",
                quickReplies: [
                    "Next evening shift",
                    "Run for a week",
                    "Assign to the GM",
                    "Attach the variance report"
                ]
            }
        },
        {
            id: "act_demo_foodcost_message_manager",
            kind: "comms",
            category: "Message Manager",
            title: "Send message to manager",
            description:
                "Send the evening-shift manager a direct, supportive message with the Classic Chicken Sandwich variance data and a request to tighten portioning on that item.",
            ctaLabel: "Send Message",
            workflow: {
                intro: "Here's the message I'll draft to the evening-shift manager:",
                bullets: [
                    {
                        label: "To",
                        value: "[Manager Name] — evening shift, 4pm–close"
                    },
                    {
                        label: "Shares",
                        value: "Classic Chicken Sandwich at 28% over standard on their shifts"
                    },
                    {
                        label: "Ask",
                        value: "Tighten portioning on that item; offer a quick re-training"
                    },
                    {
                        label: "Tone",
                        value: "Supportive and specific — coaching, not blame"
                    }
                ],
                followUp:
                    "Want to send it now, or review the draft before it goes out?",
                quickReplies: [
                    "Send it now",
                    "Review the draft",
                    "Add the variance chart",
                    "Schedule a follow-up"
                ]
            }
        }
        /* Trimmed to keep the actions list to 3 — restore if needed.
        {
            id: "act_demo_foodcost_training",
            kind: "coaching",
            category: "Schedule Training",
            title: "Schedule training task",
            description:
                "Schedule a 30-minute portioning re-training on the Classic Chicken Sandwich for the evening-shift team — the single highest-leverage move to close the gap.",
            ctaLabel: "Schedule Training",
            workflow: {
                intro: "Here's the portioning re-training task:",
                bullets: [
                    {
                        label: "Topic",
                        value: "Classic Chicken Sandwich portion control to recipe standard"
                    },
                    {
                        label: "Audience",
                        value: "Evening-shift team at 322 S Broadway"
                    },
                    {
                        label: "Length",
                        value: "30 minutes — closes the majority of the $3,200 gap"
                    },
                    {
                        label: "Follow-up",
                        value: "Re-check the item's variance one week after training"
                    }
                ],
                followUp:
                    "Want this scheduled before the next evening shift, or at the next team huddle?",
                quickReplies: [
                    "Before next evening shift",
                    "At the next huddle",
                    "Assign to the GM",
                    "Set the follow-up check"
                ]
            }
        }
        */
    ]
};

export const demoLoyaltyChurnCard: InsightCard = {
    id: "ins_demo_loyalty_churn",
    source: "PAR Punchh® · Visit Frequency & Churn Risk",
    confidence: 87,
    headlineMetric: {
        value: "$34K",
        label: "Recoverable revenue from at-risk members",
        direction: "positive"
    },
    tracker: {
        metricLabel: "Revenue recovered",
        actual: "$0",
        goal: "$34K"
    },
    title: "Loyalty Visit Frequency & Churn Risk",
    tags: ["8,400 At-Risk", "+22% vs Last Qtr", "Mid-Tier Aging Fastest"],
    narrative:
        "You have 8,400 members who were visiting at least twice a month six months ago and are now at 45+ days inactive. That's up 22% from last quarter — your at-risk pool is growing faster than you're winning people back. The segment aging out fastest is your mid-tier members (100–300 points lifetime): they engaged early, earned toward a reward, and went quiet.",
    aiNote: "The mid-tier pattern suggests they hit a wall — either the next reward felt too far away or there was nothing to pull them back in. Top-tier members (500+ points) churn at a much lower rate, but when they do lapse they almost never come back. A targeted offer to the 8,400 at-risk members now — before they hit 60 days — historically recovers about 18%, roughly $34K in recaptured revenue at your average check.",
    actions: [
        {
            id: "act_demo_churn_review_list",
            kind: "report",
            category: "Review Segment",
            title: "Review at-risk member list by segment",
            description:
                "Open the at-risk member list broken down by tier and segment, with the mid-tier (100–300 points) cohort that's aging out fastest surfaced at the top.",
            ctaLabel: "Review List",
            workflow: {
                intro: "Here's the at-risk member list by segment:",
                bullets: [
                    {
                        label: "Pool",
                        value: "8,400 members at 45+ days inactive, up 22% from last quarter"
                    },
                    {
                        label: "Fastest aging",
                        value: "Mid-tier members — 100–300 points lifetime"
                    },
                    {
                        label: "Breakdown",
                        value: "By tier, days-since-last-visit, and prior visit cadence"
                    },
                    {
                        label: "Goal",
                        value: "Target the cohort with the best recovery odds first"
                    }
                ],
                followUp:
                    "Want me to prioritize the list by recovery probability, or by lifetime value?",
                quickReplies: [
                    "Sort by recovery odds",
                    "Sort by lifetime value",
                    "Just the mid-tier",
                    "Export the list"
                ]
            }
        },
        {
            id: "act_demo_churn_campaign",
            kind: "campaign",
            category: "Launch Campaign",
            title: "Launch win-back campaign for at-risk members",
            description:
                "Create a Punchh win-back campaign targeting the 8,400 at-risk members with a personalized offer timed before they cross 60 days inactive — historically recovering ~18% (~$34K).",
            ctaLabel: "Launch Campaign",
            workflow: {
                intro: "Here's the win-back campaign for the at-risk pool:",
                bullets: [
                    {
                        label: "Audience",
                        value: "8,400 members · 45+ days inactive · formerly 2+ visits/mo"
                    },
                    {
                        label: "Offer",
                        value: "Personalized reward, redeemable before the 60-day cliff"
                    },
                    {
                        label: "Channel",
                        value: "Push + email, sent at each member's typical visit time"
                    },
                    {
                        label: "Goal",
                        value: "Recover ~18% — roughly $34K at your average check"
                    }
                ],
                followUp:
                    "Want me to tailor a distinct message for the mid-tier cohort that's aging out fastest?",
                quickReplies: [
                    "Tailor the mid-tier message",
                    "Draft the message",
                    "Set up an A/B test",
                    "How much will this cost?"
                ]
            }
        },
        {
            id: "act_demo_churn_automation",
            kind: "automation",
            category: "Set Automation",
            title: "Set up churn early-warning automation",
            description:
                "Configure a Punchh rule that auto-enrolls any member who falls below their historical visit cadence into a re-engagement journey before they reach the at-risk window.",
            ctaLabel: "Set Up Automation",
            workflow: {
                intro: "Here's the churn early-warning automation:",
                bullets: [
                    {
                        label: "Trigger",
                        value: "Member falls below their own historical visit cadence"
                    },
                    {
                        label: "Day 30",
                        value: 'Re-engagement nudge — "Your reward is waiting"'
                    },
                    {
                        label: "Day 45",
                        value: "Personalized offer before the 60-day churn cliff"
                    },
                    {
                        label: "Exit",
                        value: "Member returns and transacts — auto-removed from the journey"
                    }
                ],
                followUp:
                    "Should this run continuously for every qualifying member, or start as a 60-day pilot to measure the recovery rate?",
                quickReplies: [
                    "Run continuously",
                    "Run as a pilot",
                    "Adjust the timing",
                    "Project the impact"
                ]
            }
        }
        /* Trimmed to keep the actions list to 3 — restore if needed.
        {
            id: "act_demo_churn_tier_detail",
            kind: "analysis",
            category: "View Detail",
            title: "View tier distribution and visit cadence detail",
            description:
                "Open the tier distribution and visit-cadence view to see how each tier is trending, where members are aging out, and how reactivation rates compare across segments.",
            ctaLabel: "View Detail",
            workflow: {
                intro: "Here's the tier distribution and visit-cadence detail:",
                bullets: [
                    {
                        label: "Cadence",
                        value: "Average visits per active member per month, by tier and segment"
                    },
                    {
                        label: "Aging",
                        value: "Days-since-last-visit distribution across the member base"
                    },
                    {
                        label: "Movement",
                        value: "Members crossing into at-risk vs. the prior period"
                    },
                    {
                        label: "Recovery",
                        value: "Reactivation rate after past win-back campaigns"
                    }
                ],
                followUp:
                    "Want me to highlight the tier with the steepest cadence decline, or compare all tiers side by side?",
                quickReplies: [
                    "Highlight the steepest decline",
                    "Compare all tiers",
                    "Add a reactivation benchmark",
                    "Export the detail"
                ]
            }
        }
        */
    ]
};

export const demoMenuMixLaborCard: InsightCard = {
    id: "ins_demo_menu_mix_labor",
    source: "PAR Operations · Speed of Service",
    confidence: 89,
    headlineMetric: {
        value: "847",
        label: "Transactions hit during the 12–1:15pm breakdown",
        direction: "negative"
    },
    tracker: {
        metricLabel: "Avg speed of service",
        actual: "6:26",
        goal: "3:45"
    },
    title: "Speed of Service Breakdown — Menu Mix & Labor",
    tags: ["322 S Broadway · Tue Lunch", "38% Level 3+ Mix", "Labor −2 Positions"],
    narrative:
        "Last Tuesday at 322 S Broadway, speed of service broke down between 12:00–1:15pm — drive-thru average hit 7:42, affecting 847 transactions. Two things converged in that window: your menu mix was 38% high-complexity orders (Level 3+), driven by the current LTO running 2.1× your normal lunch ratio. At the same time, labor deployment was 2 positions below the recommended coverage threshold for that complexity level and order volume. The grill station was the choke point — KDS ticket age on grilled items averaged 4:45 against a 2:00 target.",
    aiNote: "Who: [Manager Name], Tuesday lunch shift. What: high-complexity menu mix met under-deployed labor — the kitchen could not absorb the build volume at that complexity level. Action plan: on weeks where a Level 3+ LTO is running, the scheduling template needs +1 production at the 11:30am build. PAR Intelligence can flag this automatically when forecasted LTO mix crosses the complexity threshold.",
    actions: [
        {
            id: "act_demo_sos_labor_schedule",
            kind: "labor",
            category: "Review Schedule",
            title: "Review and adjust the upcoming labor schedule for next week",
            description:
                "Open the labor schedule covering the Tuesday lunch window to review crew and manager coverage day by day and add coverage where the shortfall lines up with the slowdown.",
            ctaLabel: "Review Schedule",
            workflow: {
                intro: "Here's the labor schedule covering the flagged window:",
                bullets: [
                    {
                        label: "Coverage",
                        value: "Crew and manager coverage across the week, day by day"
                    },
                    {
                        label: "Shortfall",
                        value: "Tuesday lunch running 2 positions below the recommended threshold"
                    },
                    {
                        label: "Goal",
                        value: "Add coverage where the shortfall lines up with the slowdown"
                    }
                ],
                followUp:
                    "Want me to flag the days where scheduled hours run below target first?",
                quickReplies: [
                    "Flag days below target",
                    "Show labor % by day",
                    "Compare to last week",
                    "Export the schedule"
                ]
            }
        },
        /* Trimmed to keep the actions list to 3 — restore if needed.
        {
            id: "act_demo_sos_breakdown",
            kind: "analysis",
            category: "View Breakdown",
            title: "View breakdown by segment",
            description:
                "Open the 15-minute breakdown of the flagged window — drive-thru total, speaker, queue, and window times — alongside the transactions affected at 322 S Broadway.",
            ctaLabel: "View Breakdown",
            workflow: {
                intro: "Here's the segment breakdown for the 12:00–1:15pm window:",
                bullets: [
                    {
                        label: "Window",
                        value: "Tuesday 12:00–1:15pm at 322 S Broadway, in 15-minute slices"
                    },
                    {
                        label: "Segments",
                        value: "Drive-thru total, speaker, queue, and window times"
                    },
                    {
                        label: "Impact",
                        value: "7:42 drive-thru average · 847 transactions affected"
                    },
                    {
                        label: "Goal",
                        value: "Pinpoint exactly which segment and slice drove the breakdown"
                    }
                ],
                followUp:
                    "Want me to overlay the same window from the prior Tuesday as a baseline?",
                quickReplies: [
                    "Overlay last Tuesday",
                    "Show worst 15-min slice",
                    "Break out by channel",
                    "Export the breakdown"
                ]
            }
        },
        */
        {
            id: "act_demo_sos_overlay",
            kind: "analysis",
            category: "Complexity Overlay",
            title: "View menu complexity vs. labor deployment overlay",
            description:
                "Overlay the menu-complexity index against actual vs. recommended labor deployment for the flagged window, with the grill-station KDS bottleneck called out.",
            ctaLabel: "View Overlay",
            workflow: {
                intro: "Here's the complexity vs. labor overlay for the flagged period:",
                bullets: [
                    {
                        label: "Complexity",
                        value: "38% Level 3+ orders — 2.1× the normal lunch ratio, LTO-driven"
                    },
                    {
                        label: "Labor",
                        value: "Actual deployment 2 positions below the recommended threshold"
                    },
                    {
                        label: "Bottleneck",
                        value: "Grill station — KDS ticket age 4:45 vs. a 2:00 target"
                    },
                    {
                        label: "Goal",
                        value: "Show where the build volume outran the deployed coverage"
                    }
                ],
                followUp:
                    "Want me to model the labor level that would have held ticket times to target at that complexity?",
                quickReplies: [
                    "Model the right labor level",
                    "Show role deployment",
                    "Just the grill station",
                    "Explain the index"
                ]
            }
        },
        {
            id: "act_demo_sos_schedule",
            kind: "schedule",
            category: "Adjust Schedule",
            title: "Adjust scheduling template for high-complexity periods",
            description:
                "Update the scheduling template so weeks running a Level 3+ LTO add +1 production at the 11:30am build — with PAR Intelligence flagging it automatically when forecasted LTO mix crosses the complexity threshold.",
            ctaLabel: "Adjust Template",
            workflow: {
                intro: "Here's the scheduling-template change for high-complexity periods:",
                bullets: [
                    {
                        label: "Trigger",
                        value: "Any week a Level 3+ LTO is running"
                    },
                    {
                        label: "Change",
                        value: "+1 production position at the 11:30am build"
                    },
                    {
                        label: "Automation",
                        value: "Auto-flag when forecasted LTO mix crosses the complexity threshold"
                    },
                    {
                        label: "Goal",
                        value: "Absorb the build volume before lunch peak, protecting ticket times"
                    }
                ],
                followUp:
                    "Want this applied to just 322 S Broadway, or every location running the Level 3+ LTO?",
                quickReplies: [
                    "Just this location",
                    "All LTO locations",
                    "Set the auto-flag",
                    "Preview the new template"
                ]
            }
        }
        /* Trimmed to keep the actions list to 3 — restore if needed.
        {
            id: "act_demo_sos_message_manager",
            kind: "comms",
            category: "Message Manager",
            title: "Message manager",
            description:
                "Send the Tuesday lunch-shift manager the breakdown for the flagged window with the who/what/action plan, so they can prep coverage for the next high-complexity period.",
            ctaLabel: "Send Message",
            workflow: {
                intro: "Here's the message I'll draft to the lunch-shift manager:",
                bullets: [
                    {
                        label: "To",
                        value: "[Manager Name] — Tuesday lunch shift, 322 S Broadway"
                    },
                    {
                        label: "Shares",
                        value: "12:00–1:15pm breakdown: 38% Level 3+ mix, labor −2, grill bottleneck"
                    },
                    {
                        label: "Action",
                        value: "+1 production at the 11:30am build on Level 3+ LTO weeks"
                    },
                    {
                        label: "Tone",
                        value: "Supportive and specific — a clear plan, not blame"
                    }
                ],
                followUp: "Want to send it now, or review the draft first?",
                quickReplies: [
                    "Send it now",
                    "Review the draft",
                    "Attach the overlay",
                    "Schedule a follow-up"
                ]
            }
        }
        */
    ]
};

export interface InsightCategory {
    id: string;
    label: string;
    cards: InsightCard[];
}

export const insightCategories: InsightCategory[] = [
    {
        id: "punchh",
        label: "Punchh Loyalty",
        cards: [
            enrollmentDropCard,
            winBackCard,
            silverTierCard,
            offerIncrementalityCard
        ]
    },
    {
        id: "labor",
        label: "Labor",
        cards: [
            laborDinnerOvertimeCard,
            laborLiveStaffingCard,
            laborStoreOutliersCard
        ]
    },
    {
        id: "cogs",
        label: "COGS",
        cards: [
            cogsVarianceTrendCard,
            cogsTopOffenderCard,
            cogsClassificationCard,
            cogsRecoveryCard,
            demoHighFoodCostCard
        ]
    },
    {
        id: "drivethru_sos",
        label: "Drive-Thru SOS",
        cards: [
            sosPeakSpikeCard,
            sosTrendSlowingCard,
            sosThursdayStaffingCard,
            sosLunchDaypartCard,
            sosStoreOutliersCard,
            demoMenuMixLaborCard
        ]
    },
    {
        id: "menu",
        label: "Menu",
        cards: []
    },
    {
        id: "sell",
        label: "Fraud",
        cards: [demoStoreHealthCard]
    },
    {
        id: "loyalty_churn",
        label: "Loyalty Churn",
        cards: [demoLoyaltyChurnCard]
    },
    {
        id: "campaign_effectiveness",
        label: "Campaign Effectiveness",
        cards: [demoPromotionEffectivenessCard]
    }
];

export const allInsightCards: InsightCard[] = insightCategories.flatMap(
    (category) => category.cards
);

export function findInsightCardById(id: string): InsightCard | undefined {
    return allInsightCards.find((card) => card.id === id);
}
